import React, { useState, useEffect } from 'react';
import './Courses.css';
import { useParams } from "react-router-dom";
import { apiFetch } from '../../utils/api';

// Helper function to map available courses
const mapAvailableCourses = (availData) =>
  availData.map(course => ({
    id: course.course_id,
    name: course.course_title,
    video_count: course.video_count,
    total_duration: course.total_duration,
    department: course.department_name,
    credits: 0,
    isExternal: false
  }));

// Helper function to map external courses
const mapExternalCourses = (extAvailData) =>
  extAvailData.map(course => ({
    id: course.id,
    name: course.name,
    provider: course.provider,
    cost: course.cost,
    enrollment_url: course.enrollment_url,
    estimated_duration: course.estimated_duration,
    department: course.department_name || 'Unknown',
    credits: 0,
    isExternal: true
  }));

// Helper function to map selected internal courses
const mapSelectedInternalCourses = (selectedData, mappedAvailable) =>
  selectedData.courses.map(c => {
    const courseInfo = mappedAvailable.find(ac => ac.id === c.course_id) || {
      name: 'Unknown',
      video_count: 0,
      total_duration: 0,
      department: ''
    };
    return {
      lpcId: c.id,
      id: c.course_id,
      order: c.order,
      name: courseInfo.name,
      video_count: courseInfo.video_count,
      total_duration: courseInfo.total_duration,
      department: courseInfo.department,
      startDate: c.start_date ? c.start_date.split('T')[0] : '',
      endDate: c.end_date ? c.end_date.split('T')[0] : '',
      credits: c.credits,
      isMandatory: c.is_mandatory,
      isExternal: false
    };
  });

// Helper function to map selected external courses
const mapSelectedExternalCourses = (extSelectedData, mappedExternal) =>
  extSelectedData.map(c => {
    const courseInfo = mappedExternal.find(ac => ac.id === c.external_course_id) || {
      name: c.name || 'Unknown',
      provider: c.provider || '',
      estimated_duration: c.estimated_duration || 0,
      department: c.department_name || 'Unknown'
    };
    return {
      lpcId: c.id,
      id: c.external_course_id,
      order: c.order,
      name: courseInfo.name,
      provider: courseInfo.provider,
      estimated_duration: courseInfo.estimated_duration,
      department: courseInfo.department,
      startDate: c.start_date ? c.start_date.split('T')[0] : '',
      endDate: c.end_date ? c.end_date.split('T')[0] : '',
      credits: c.credits,
      isMandatory: c.is_mandatory,
      isExternal: true
    };
  });

// Helper function to sort selected courses
const sortSelectedCourses = (courses) =>
  [...courses].sort((a, b) => {
    const orderA = a.order ?? Infinity;
    const orderB = b.order ?? Infinity;
    return orderA - orderB;
  });

const Courses = () => {
  console.log('Rendering Courses');
  const { name } = useParams();

  // Split slug and id (last part after the last "-")
  const learningPathId = name.split("-").pop();
  const learningPathSlug = name.replace(`-${learningPathId}`, "");

  console.log("Learning Path ID:", learningPathId);
  console.log("Learning Path Slug:", learningPathSlug);

  const [activeTab, setActiveTab] = useState('lAndDCourses');
  const [availableCourses, setAvailableCourses] = useState([]);
  const [availableExternalCourses, setAvailableExternalCourses] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [removedCourses, setRemovedCourses] = useState([]);
  const [removedExternalCourses, setRemovedExternalCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState('success');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newCourse, setNewCourse] = useState({
    name: '',
    description: '',
    provider: '',
    cost: '',
    enrollment_url: '',
    estimated_duration: '',
    created_by: localStorage.getItem("userEmail") || ''
  });

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch internal courses
        const availData = await apiFetch(`${window._env_.API_BASE_URL}/courses/summary`);
        const mappedAvailable = mapAvailableCourses(availData);
        setAvailableCourses(mappedAvailable);

        // Fetch external courses
        const extAvailData = await apiFetch(`${window._env_.API_BASE_URL}/ext_custcourses/external-courses/`);
        const mappedExternal = mapExternalCourses(extAvailData);
        setAvailableExternalCourses(mappedExternal);

        if (learningPathId) {
          await loadSelectedCourses(learningPathId, mappedAvailable, mappedExternal);
        }
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };
    fetchData();
  }, [learningPathId]);

  // Helper function to load selected courses
  const loadSelectedCourses = async (pathId, mappedAvailable, mappedExternal) => {
    // Fetch existing internal courses
    const selectedData = await apiFetch(`${window._env_.API_BASE_URL}/learning_paths/learning-path-courses/${pathId}`);
    const mappedSelectedInternal = mapSelectedInternalCourses(selectedData, mappedAvailable);

    // Fetch existing external courses
    const extSelectedData = await apiFetch(`${window._env_.API_BASE_URL}/ext_custcourses/learning_paths/learning-path-external-courses/${pathId}`);
    const mappedSelectedExternal = mapSelectedExternalCourses(extSelectedData, mappedExternal);

    // Sort selected courses by order (ascending, nulls last)
    const sortedSelected = sortSelectedCourses([...mappedSelectedInternal, ...mappedSelectedExternal]);
    setSelectedCourses(sortedSelected);
  };

  // Handle input changes for create course form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewCourse(prev => ({ ...prev, [name]: value }));
  };

  // Handle create course submission
  const handleCreateCourse = async (e) => {
    e.preventDefault();
    setIsCreating(true);
    try {
      await createExternalCourse();
      await refreshExternalCoursesList();
      showSuccessMessage('External course created successfully');
      resetCreateForm();
      setShowCreateModal(false);
    } catch (err) {
      showErrorMessage(err.message || 'An error occurred while creating the course');
    } finally {
      setIsCreating(false);
    }
  };

  // Helper function to create external course
  const createExternalCourse = async () => {
    const response = await apiFetch(`${window._env_.API_BASE_URL}/ext_custcourses/external-courses/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: newCourse.name,
        description: newCourse.description || null,
        provider: newCourse.provider,
        cost: parseFloat(newCourse.cost) || 0,
        enrollment_url: newCourse.enrollment_url || null,
        estimated_duration: parseInt(newCourse.estimated_duration) || null,
        created_by: newCourse.created_by
      })
    });
    return response;
  };

  // Helper function to refresh external courses list
  const refreshExternalCoursesList = async () => {
    const extAvailData = await apiFetch(`${window._env_.API_BASE_URL}/ext_custcourses/external-courses/`);
    const mappedExternal = mapExternalCourses(extAvailData);
    setAvailableExternalCourses(mappedExternal);
  };

  // Helper function to show success message
  const showSuccessMessage = (message) => {
    setSnackbarMessage(message);
    setSnackbarType('success');
    setShowSnackbar(true);
    setTimeout(() => setShowSnackbar(false), 3000);
  };

  // Helper function to show error message
  const showErrorMessage = (message) => {
    setSnackbarMessage(message);
    setSnackbarType('error');
    setShowSnackbar(true);
    setTimeout(() => setShowSnackbar(false), 3000);
  };

  // Helper function to reset create form
  const resetCreateForm = () => {
    setNewCourse({
      name: '',
      description: '',
      provider: '',
      cost: '',
      enrollment_url: '',
      estimated_duration: '',
      created_by: localStorage.getItem("userEmail") || ''
    });
  };

  const departments = [
    ...new Set([
      ...availableCourses.map(c => c.department),
      ...availableExternalCourses.map(c => c.department)
    ])
  ];

  const filteredAvailable = activeTab === 'lAndDCourses'
    ? availableCourses.filter(c =>
      c.name.toLowerCase().includes(search.toLowerCase()) &&
      (!selectedDepartment || c.department === selectedDepartment)
    )
    : availableExternalCourses.filter(c =>
      c.name.toLowerCase().includes(search.toLowerCase())
    );

  const addCourse = (course) => {
    if (!selectedCourses.find(sc => sc.id === course.id && sc.isExternal === course.isExternal)) {
      // Assign order as the last position +1 (or 1 if empty)
      const newOrder = selectedCourses.length > 0 ? Math.max(...selectedCourses.map(sc => sc.order ?? 0)) + 1 : 1;
      setSelectedCourses([...selectedCourses, {
        ...course,
        order: newOrder,
        startDate: '',
        endDate: '',
        credits: course.credits ?? 0,
        isMandatory: false
      }]);
    }
  };

  const removeCourse = (id, isExternal) => {
    const courseToRemove = selectedCourses.find(sc => sc.id === id && sc.isExternal === isExternal);
    if (courseToRemove && courseToRemove.lpcId) {
      if (isExternal) {
        setRemovedExternalCourses([...removedExternalCourses, courseToRemove.lpcId]);
      } else {
        setRemovedCourses([...removedCourses, courseToRemove.lpcId]);
      }
    }
    // After removal, reassign orders to remaining courses
    const remainingCourses = selectedCourses.filter(sc => !(sc.id === id && sc.isExternal === isExternal));
    const reordered = remainingCourses.map((sc, idx) => ({ ...sc, order: idx + 1 }));
    setSelectedCourses(reordered);
  };

  const updateCredits = (id, isExternal, credits) => {
    const newCredits = Math.max(Number(credits), 0) || 0;
    setSelectedCourses(selectedCourses.map(sc =>
      sc.id === id && sc.isExternal === isExternal ? { ...sc, credits: newCredits } : sc
    ));
  };

  const updateStartDate = (id, isExternal, startDate) => {
    setSelectedCourses(selectedCourses.map(sc =>
      sc.id === id && sc.isExternal === isExternal ? { ...sc, startDate } : sc
    ));
  };

  const updateEndDate = (id, isExternal, endDate) => {
    setSelectedCourses(selectedCourses.map(sc =>
      sc.id === id && sc.isExternal === isExternal ? { ...sc, endDate } : sc
    ));
  };

  const toggleMandatory = (id, isExternal) => {
    setSelectedCourses(selectedCourses.map(sc =>
      sc.id === id && sc.isExternal === isExternal ? { ...sc, isMandatory: !sc.isMandatory } : sc
    ));
  };

  const moveCourse = (index, direction) => {
    const newSelected = [...selectedCourses];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < newSelected.length) {
      [newSelected[index], newSelected[newIndex]] = [newSelected[newIndex], newSelected[index]];
      // Reassign orders after swap
      const reordered = newSelected.map((sc, idx) => ({ ...sc, order: idx + 1 }));
      setSelectedCourses(reordered);
    }
  };

  const handleSave = async () => {
    if (!learningPathId) {
      showErrorMessage("Learning Path ID is missing!");
      return;
    }

    if (!validateSelectedCourses()) {
      return;
    }

    setSaving(true);
    try {
      await saveAllCourses();
      showSuccessMessage("Saved learning path successfully");
    } catch (err) {
      console.error("Error saving learning path:", err);
      showErrorMessage(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Helper function to validate selected courses
  const validateSelectedCourses = () => {
    for (const course of selectedCourses) {
      if (!course.startDate || !course.endDate) {
        showErrorMessage(`Missing start or end date for course: ${course.name}`);
        return false;
      }

      if (new Date(course.endDate) < new Date(course.startDate)) {
        showErrorMessage(`End date cannot be before start date for course: ${course.name}`);
        return false;
      }
    }
    return true;
  };

  // Helper function to save all courses
  const saveAllCourses = async () => {
    const userEmail = localStorage.getItem("userEmail") || '';
    const internalCourses = selectedCourses.filter(sc => !sc.isExternal);
    const externalCourses = selectedCourses.filter(sc => sc.isExternal);
    const newInternalCourses = internalCourses.filter(sc => !sc.lpcId);
    const updatedInternalCourses = internalCourses.filter(sc => sc.lpcId);
    const newExternalCourses = externalCourses.filter(sc => !sc.lpcId);
    const updatedExternalCourses = externalCourses.filter(sc => sc.lpcId);

    // Delete removed courses
    await deleteRemovedCourses(userEmail);

    // Update existing courses
    await updateExistingCourses(updatedInternalCourses, updatedExternalCourses, userEmail);

    // Add new courses
    await addNewCourses(newInternalCourses, newExternalCourses, userEmail);

    // Clear removed courses
    setRemovedCourses([]);
    setRemovedExternalCourses([]);
  };

  // Helper function to delete removed courses
  const deleteRemovedCourses = async (userEmail) => {
    if (removedCourses.length > 0) {
      await apiFetch(
        `${window._env_.API_BASE_URL}/learning_paths/delete-learning-path-courses`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids: removedCourses }),
        }
      );
    }

    if (removedExternalCourses.length > 0) {
      await apiFetch(
        `${window._env_.API_BASE_URL}/ext_custcourses/learning_paths/delete-learning-path-external-courses`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids: removedExternalCourses, deleted_by: userEmail }),
        }
      );
    }
  };

  // Helper function to update existing courses
  const updateExistingCourses = async (updatedInternalCourses, updatedExternalCourses, userEmail) => {
    if (updatedInternalCourses.length > 0) {
      const updatePayload = {
        courses: updatedInternalCourses.map(c => ({
          id: c.lpcId,
          start_date: c.startDate,
          end_date: c.endDate,
          credits: c.credits,
          is_mandatory: c.isMandatory,
          order: c.order
        }))
      };
      await apiFetch(
        `${window._env_.API_BASE_URL}/learning_paths/update-learning-path-courses`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updatePayload),
        }
      );
    }

    if (updatedExternalCourses.length > 0) {
      const updatePayload = {
        courses: updatedExternalCourses.map(c => ({
          id: c.lpcId,
          start_date: c.startDate,
          end_date: c.endDate,
          credits: c.credits,
          is_mandatory: c.isMandatory,
          order: c.order,
          updated_by: userEmail
        }))
      };
      await apiFetch(
        `${window._env_.API_BASE_URL}/ext_custcourses/learning_paths/update-learning-path-external-courses`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updatePayload),
        }
      );
    }
  };

  // Helper function to add new courses
  const addNewCourses = async (newInternalCourses, newExternalCourses, userEmail) => {
    if (newInternalCourses.length > 0) {
      await addNewInternalCourses(newInternalCourses);
    }

    if (newExternalCourses.length > 0) {
      await addNewExternalCourses(newExternalCourses, userEmail);
    }
  };

  // Helper function to add new internal courses
  const addNewInternalCourses = async (newInternalCourses) => {
    const addPayload = {
      courses: newInternalCourses.map(c => ({
        learning_path_id: parseInt(learningPathId, 10),
        course_id: c.id,
        start_date: c.startDate,
        end_date: c.endDate,
        credits: c.credits,
        is_mandatory: c.isMandatory,
        order: c.order
      }))
    };
    const addData = await apiFetch(
      `${window._env_.API_BASE_URL}/learning_paths/add-learning-path-courses`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(addPayload),
      }
    );
    const newSelected = selectedCourses.map(sc => {
      if (!sc.lpcId && !sc.isExternal) {
        const inserted = addData.courses.find(ic => ic.course_id === sc.id);
        if (inserted) {
          return { ...sc, lpcId: inserted.id };
        }
      }
      return sc;
    });
    setSelectedCourses(newSelected);
  };

  // Helper function to add new external courses
  const addNewExternalCourses = async (newExternalCourses, userEmail) => {
    const addPayload = {
      courses: newExternalCourses.map(c => ({
        learning_path_id: parseInt(learningPathId, 10),
        external_course_id: c.id,
        start_date: c.startDate,
        end_date: c.endDate,
        credits: c.credits,
        is_mandatory: c.isMandatory,
        order: c.order,
        assigned_by: userEmail
      }))
    };
    const addData = await apiFetch(
      `${window._env_.API_BASE_URL}/ext_custcourses/learning-path-external-courses/`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(addPayload),
      }
    );
    const newSelected = selectedCourses.map(sc => {
      if (!sc.lpcId && sc.isExternal) {
        const inserted = addData.find(ic => ic.external_course_id === sc.id);
        if (inserted) {
          return { ...sc, lpcId: inserted.id };
        }
      }
      return sc;
    });
    setSelectedCourses(newSelected);
  };

  const totalCredits = selectedCourses.reduce((sum, sc) => sum + (sc.credits ?? 0), 0);

  if (loading) {
    return (
      <div className="mmlp-courses-loading">
        <svg className="mmlp-visby-spinner" viewBox="0 0 50 50" aria-label="Loading">
          <circle className="mmlp-visby-path" cx="25" cy="25" r="20" fill="none" />
        </svg>
        <div className="mmlp-courses-loading-text">Loading...</div>
      </div>
    );
  }

  if (error) return <div>Error: {error}</div>;

  return (
    <div className="mmlp-learning-path-container">
      <div className="mmlp-header-section">
        <h2 className="mmlp-main-title">Create Learning Path</h2>
        <p className="mmlp-subtitle">Build a customized learning journey by selecting courses and setting your timeline</p>
      </div>

      <div className="mmlp-content-wrapper">
        {/* Left: Add Courses */}
        <div className="mmlp-left-panel">
          <div className="mmlp-panel-header">
            <div className="mmlp-tabs-container">
              <div className="mmlp-tabs">
                <button
                  className={`mmlp-tab ${activeTab === 'lAndDCourses' ? 'mmlp-tab-active' : ''}`}
                  onClick={() => setActiveTab('lAndDCourses')}
                >
                  L&D Courses
                </button>
                <button
                  className={`mmlp-tab ${activeTab === 'externalCourses' ? 'mmlp-tab-active' : ''}`}
                  onClick={() => setActiveTab('externalCourses')}
                >
                  Custom Courses
                </button>
              </div>
            </div>
            <p className="mmlp-section-subtitle">Search courses to add to your path</p>
            <div className="mmlp-filter-group">
              <input
                type="text"
                placeholder="Search courses..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="mmlp-search-input"
              />
              {activeTab === 'lAndDCourses' && (
                <select
                  value={selectedDepartment}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                  className="mmlp-department-select"
                >
                  <option value="">All Departments</option>
                  {departments.map(dep => (
                    <option key={dep} value={dep}>{dep}</option>
                  ))}
                </select>
              )}
              {activeTab === 'externalCourses' && (
                <button
                  className="mmlp-create-course-button"
                  onClick={() => setShowCreateModal(true)}
                >
                  Create Ext Course
                </button>
              )}
            </div>
          </div>
          <div className="mmlp-course-list-wrapper">
            <ul className="mmlp-course-list">
              {filteredAvailable.map(course => (
                <li key={`${course.id}-${course.isExternal}`} className="mmlp-available-course-item">
                  <div className="mmlp-course-info">
                    <h4>{course.name}</h4>
                    <p className="mmlp-course-details">
                      {course.isExternal
                        ? `${course.estimated_duration || 'N/A'} hours • ${course.provider} • ₹${course.cost || '0.00'}`
                        : `${course.total_duration} • ${course.video_count} videos • ${course.department}`}
                    </p>
                    {course.isExternal && course.enrollment_url && (
                      <p className="mmlp-enrollment-url">
                        <a href={course.enrollment_url} target="_blank" rel="noopener noreferrer">Course Link</a>
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => addCourse(course)}
                    className="mmlp-add-button"
                  >
                    Add
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Right: Selected Courses */}
        <div className="mmlp-right-panel">
          <div className="mmlp-selected-header">
            <div className="mmlp-selected-title-wrapper">
              <h3>Selected Courses ({selectedCourses.length})</h3>
              <p className="mmlp-section-subtitle">Click ↑ ↓ to reorder your learning sequence</p>
            </div>
            <div className="mmlp-total-credits">{totalCredits} Total Credits</div>
          </div>

          <ul className="mmlp-course-list">
            {selectedCourses.map((course, index) => (
              <li key={`${course.id}-${course.isExternal}`} className="mmlp-selected-course-item">
                <div className="mmlp-course-row">
                  <div className="mmlp-course-order">
                    <div className="mmlp-order-number">{index + 1}</div>
                    <div className="mmlp-move-buttons">
                      <button
                        onClick={() => moveCourse(index, 'up')}
                        disabled={index === 0}
                        className="mmlp-move-button"
                      >
                        ↑
                      </button>
                      <button
                        onClick={() => moveCourse(index, 'down')}
                        disabled={index === selectedCourses.length - 1}
                        className="mmlp-move-button"
                      >
                        ↓
                      </button>
                    </div>
                  </div>

                  <div className="mmlp-course-content">
                    <h4 className="mmlp-course-name">{course.name} {course.isExternal && <span className="mmlp-external-label">(External)</span>}</h4>
                    <div className="mmlp-course-inputs">
                      <div className="mmlp-input-group">
                        <label htmlFor={`start-date-${course.id}-${course.isExternal}`} className="mmlp-input-label">Start Date</label>
                        <input
                          id={`start-date-${course.id}-${course.isExternal}`}
                          type="date"
                          value={course.startDate}
                          onChange={(e) => updateStartDate(course.id, course.isExternal, e.target.value)}
                          className="mmlp-date-input"
                        />
                      </div>
                      <div className="mmlp-input-group">
                        <label htmlFor={`end-date-${course.id}-${course.isExternal}`} className="mmlp-input-label">End Date</label>
                        <input
                          id={`end-date-${course.id}-${course.isExternal}`}
                          type="date"
                          value={course.endDate}
                          min={course.startDate}
                          onChange={(e) => updateEndDate(course.id, course.isExternal, e.target.value)}
                          className="mmlp-date-input"
                        />
                      </div>
                      <div className="mmlp-input-group">
                        <label htmlFor={`credits-${course.id}-${course.isExternal}`} className="mmlp-input-label">Credits</label>
                        <input
                          id={`credits-${course.id}-${course.isExternal}`}
                          type="number"
                          value={course.credits}
                          onChange={(e) => updateCredits(course.id, course.isExternal, e.target.value)}
                          className="mmlp-credits-input"
                          min="0"
                        />
                      </div>
                      <div className="mmlp-input-group mmlp-checkbox-group">
                        <label htmlFor={`mandatory-${course.id}-${course.isExternal}`} className="mmlp-input-label" title="Mandatory course">*</label>
                        <input
                          id={`mandatory-${course.id}-${course.isExternal}`}
                          type="checkbox"
                          checked={course.isMandatory}
                          onChange={() => toggleMandatory(course.id, course.isExternal)}
                          className="mmlp-mandatory-checkbox"
                        />
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => removeCourse(course.id, course.isExternal)}
                    className="mmlp-remove-button"
                  >
                    ×
                  </button>
                </div>
              </li>
            ))}
          </ul>

          <button
            className="mmlp-save-button"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Learning Path'}
          </button>
        </div>
      </div>

      {/* Create External Course Modal */}
      {showCreateModal && (
        <div className="mmlp-modal">
          <div className="mmlp-modal-content">
            <button
              className="mmlp-modal-close-button"
              onClick={() => setShowCreateModal(false)}
              aria-label="Close modal"
            >
              ×
            </button>
            <h3>Create Custom Course</h3>
            <form onSubmit={handleCreateCourse}>
              <div className="mmlp-form-group">
                <label htmlFor="course-name">Name</label>
                <input
                  id="course-name"
                  type="text"
                  name="name"
                  value={newCourse.name}
                  onChange={handleInputChange}
                  required
                  className="mmlp-form-input"
                />
              </div>
              <div className="mmlp-form-group">
                <label htmlFor="course-description">Description</label>
                <textarea
                  id="course-description"
                  name="description"
                  value={newCourse.description}
                  onChange={handleInputChange}
                  className="mmlp-form-input"
                />
              </div>
              <div className="mmlp-form-group">
                <label htmlFor="course-provider">Provider</label>
                <input
                  id="course-provider"
                  type="text"
                  name="provider"
                  value={newCourse.provider}
                  onChange={handleInputChange}
                  required
                  className="mmlp-form-input"
                />
              </div>
              <div className="mmlp-form-group">
                <label htmlFor="course-cost">Cost (INR)</label>
                <input
                  id="course-cost"
                  type="number"
                  name="cost"
                  value={newCourse.cost}
                  onChange={handleInputChange}
                  required
                  min="0"
                  step="0.01"
                  className="mmlp-form-input"
                />
              </div>
              <div className="mmlp-form-group">
                <label htmlFor="course-enrollment-url">Enrollment URL</label>
                <input
                  id="course-enrollment-url"
                  type="url"
                  name="enrollment_url"
                  value={newCourse.enrollment_url}
                  onChange={handleInputChange}
                  className="mmlp-form-input"
                />
              </div>
              <div className="mmlp-form-group">
                <label htmlFor="course-estimated-duration">Estimated Duration (hours)</label>
                <input
                  id="course-estimated-duration"
                  type="number"
                  name="estimated_duration"
                  value={newCourse.estimated_duration}
                  onChange={handleInputChange}
                  min="0"
                  className="mmlp-form-input"
                />
              </div>
              <div className="mmlp-form-buttons">
                <button
                  type="submit"
                  className="mmlp-form-submit-button"
                  disabled={isCreating}
                >
                  {isCreating ? 'Creating...' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="mmlp-form-cancel-button"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showSnackbar && (
        <div className={`mmlp-snackbar-top mmlp-snackbar-${snackbarType}`}>
          {snackbarMessage}
        </div>
      )}
    </div>
  );
};

export default Courses;